/**
 * One-shot intent: open a map and select a landmark (#172 reverse link).
 * The Relationships row sets this before navigating; MapEditorHost/MapEditor
 * consume it once the routed map buffer is ready.
 */

import { shallowRef, type ShallowRef } from "vue";
import { isNothing, just, nothing, type Maybe } from "./maybe";

export type MapLandmarkFocus = {
  readonly mapFile: string;
  readonly landmarkId: string;
};

/** Reactive pending focus. Read `.value` inside computed/watch so Vue tracks it. */
export const pendingMapLandmarkFocus: ShallowRef<Maybe<MapLandmarkFocus>> =
  shallowRef(nothing);

/** Queue focus for `mapFile` / `landmarkId` (replaces any prior pending focus). */
export function requestMapLandmarkFocus(
  mapFile: string,
  landmarkId: string,
): void {
  const map = mapFile.trim();
  const id = landmarkId.trim();
  if (map.length === 0 || id.length === 0) {
    pendingMapLandmarkFocus.value = nothing;
    return;
  }
  pendingMapLandmarkFocus.value = just({ mapFile: map, landmarkId: id });
}

/**
 * If pending focus targets `mapFile`, return its landmark id and clear.
 * Otherwise leave pending alone (wrong map still opening).
 */
export function takeMapLandmarkFocus(mapFile: string): Maybe<string> {
  const current = pendingMapLandmarkFocus.value;
  if (isNothing(current)) return nothing;
  if (current.value.mapFile !== mapFile) return nothing;
  const id = current.value.landmarkId;
  pendingMapLandmarkFocus.value = nothing;
  return just(id);
}

/** Test helper. */
export function clearMapLandmarkFocus(): void {
  pendingMapLandmarkFocus.value = nothing;
}
